import React, { useState, useEffect } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnResizeMode,
  SortingState,
  getPaginationRowModel,
} from '@tanstack/react-table';

interface TableProps {
  headers: string[];
  rows: any[];
  caption?: string;
  theme?: 'modern' | 'minimal' | 'bordered' | 'striped';
  wrapText?: boolean;
}

// Helper type for data rows
type DataRow = Record<string, string>;

// Helper function to parse markdown links and render as clickable links
const renderCellContent = (value: string): React.ReactNode => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  // Regex to match markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

  // Check if the value contains any markdown links
  if (!markdownLinkRegex.test(value)) {
    return value;
  }

  // Reset regex lastIndex after test
  markdownLinkRegex.lastIndex = 0;

  // Split the string and replace markdown links with React elements
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = markdownLinkRegex.exec(value)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(value.slice(lastIndex, match.index));
    }

    // Add the link as a React element
    const linkText = match[1];
    const linkUrl = match[2];
    parts.push(
      <a
        key={`link-${keyIndex++}`}
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline"
        onClick={(e) => e.stopPropagation()} // Prevent row click events
      >
        {linkText}
      </a>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text after the last link
  if (lastIndex < value.length) {
    parts.push(value.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : value;
};

const ResizableTable: React.FC<TableProps> = ({
  headers,
  rows,
  caption = '',
  theme = 'modern',
  wrapText = false,
}) => {
  // State management
  const [data, setData] = useState<DataRow[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  console.log(`headers: ${headers}`);

  // Format data when rows or headers change
  useEffect(() => {
    const formattedData = rows.map((row) => {
      const rowData: DataRow = {};
      
      // Handle different row data formats
      if (Array.isArray(row)) {
        // Normal array format
        headers.forEach((header, index) => {
          rowData[`col${index}`] = String(row[index] || '');
        });
      } else if (row && typeof row === 'object') {
        // Process objects with various potential key formats
        headers.forEach((header, index) => {
          // Try all possible key formats in order of likelihood:
          // 1. String version of index ("0", "1", "2")
          // 2. Numeric index directly (0, 1, 2)
          // 3. "col{index}" format from our table component
          // 4. Empty string as fallback
          let value = '';
          const indexStr = index.toString();
          
          if (row[indexStr] !== undefined) {
            value = row[indexStr];
          } else if (row[index] !== undefined) {
            value = row[index];
          } else if (row[`col${index}`] !== undefined) {
            value = row[`col${index}`];
          }
          
          // Convert the value to string, but don't add index prefix
          rowData[`col${index}`] = String(value);
        });
      } else {
        // Fallback for unexpected formats
        rowData[`col0`] = String(row || '');
      }
      
      return rowData;
    });
    console.log(`formattedData: ${formattedData}`);
    setData(formattedData);
  }, [rows, headers]);

  // Column helper to create column definitions
  const columnHelper = createColumnHelper<DataRow>();

  // Define columns based on headers
  const columns = headers.map((header, index) =>
    columnHelper.accessor(`col${index}`, {
      header: () => header,
      cell: info => (
        <div
          title={info.getValue()}
          className={wrapText ? "max-h-40 overflow-y-auto" : ""}
        >
          {renderCellContent(info.getValue())}
        </div>
      ),
      size: 150, // Default size
      minSize: 50, // Min column width
      maxSize: 500, // Max column width
    })
  );

  // Create the table
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    columnResizeMode,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Determine theme-specific styling
  const getTableClassName = () => {
    const baseClasses = "w-full text-left border-collapse my-4 font-sans text-sm";
    
    switch (theme) {
      case 'modern':
        return `${baseClasses} rounded-lg overflow-hidden shadow-md`;
      case 'minimal':
        return `${baseClasses} border-t border-b border-gray-200 dark:border-gray-700`;
      case 'bordered':
        return `${baseClasses} border border-gray-200 dark:border-gray-700`;
      case 'striped':
        return `${baseClasses}`;
      default:
        return baseClasses;
    }
  };

  const getHeaderClassName = () => {
    const baseClasses = "relative px-4 py-3 font-medium";
    
    switch (theme) {
      case 'modern':
        return `${baseClasses} bg-blue-50 dark:bg-blue-900 dark:bg-opacity-40 text-blue-900 dark:text-blue-100`;
      case 'minimal':
        return `${baseClasses} border-b-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200`;
      case 'bordered':
        return `${baseClasses} bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200`;
      case 'striped':
        return `${baseClasses} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200`;
      default:
        return baseClasses;
    }
  };

  const getCellClassName = (rowIndex: number) => {
    const baseClasses = wrapText 
      ? "px-4 py-3 overflow-hidden break-words whitespace-normal" 
      : "px-4 py-3 overflow-hidden text-ellipsis whitespace-nowrap";
    
    switch (theme) {
      case 'modern':
        return `${baseClasses} border-t border-gray-100 dark:border-gray-800`;
      case 'minimal':
        return `${baseClasses} border-b border-gray-100 dark:border-gray-800`;
      case 'bordered':
        return `${baseClasses} border border-gray-200 dark:border-gray-700`;
      case 'striped':
        return `${baseClasses} ${rowIndex % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`;
      default:
        return baseClasses;
    }
  };

  return (
    <div className="my-6">
      {caption && (
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 p-2 text-left mb-2">
          {caption}
        </div>
      )}
      
      <div className="relative">
        {/* Mobile swipe indicator */}
        <div className="md:hidden text-xs text-center text-gray-500 dark:text-gray-400 mb-2 flex items-center justify-center px-2 w-full">
          <span className="inline-flex items-center">
            <svg className="w-4 h-4 mr-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span className="whitespace-nowrap mr-1">Swipe</span>
          </span>
          <span className="inline-flex items-center">
            <svg className="w-6 h-4 mx-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
            <span className="whitespace-nowrap">for more</span>
          </span>
        </div>
        
        <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className={getTableClassName()} style={{ width: table.getCenterTotalSize(), minWidth: '100%', tableLayout: 'fixed' }}>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th 
                      key={header.id}
                      className={`${getHeaderClassName()} cursor-pointer select-none`}
                      style={{
                        width: header.getSize(),
                        position: 'relative',
                        minWidth: '100px', // Minimum header width for mobile
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center justify-between">
                        <div className="truncate max-w-full">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        {header.column.getIsSorted() && (
                          <span className="ml-2 flex-shrink-0">
                            {header.column.getIsSorted() === 'asc' ? ' 🔼' : ' 🔽'}
                          </span>
                        )}
                      </div>
                      {/* Resizer - visible only on hover */}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none 
                          ${header.column.getIsResizing() ? 'bg-blue-500' : 'hover:bg-blue-300'}`}
                      />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, i) => (
                <tr 
                  key={row.id}
                  className={theme === 'modern' ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors' : ''}
                >
                  {row.getVisibleCells().map(cell => (
                    <td 
                      key={cell.id}
                      className={getCellClassName(i)}
                      style={{ 
                        width: cell.column.getSize(),
                        minWidth: '100px', // Minimum cell width for mobile
                        maxWidth: '100vw',
                        whiteSpace: wrapText ? 'normal' : 'nowrap'
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination - only shown when page count > 1 */}
      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <div className="flex gap-2">
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {'<<'}
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {'<'}
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {'>'}
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {'>>'}
            </button>
          </div>
          <div>
            Page{' '}
            <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </strong>
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value))
            }}
            className="px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
          >
            {[10, 25, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default ResizableTable;